import React, { useState, useEffect, useRef } from 'react';

import { CLICK_OUTSIDE_EVENT } from '../../../shared/constants/event.constants';

import DiscountApplied from './DiscountApplied';

export interface ActiveCode {
  code: string;
  discount?: string;
  onRemove?: () => void;
}

export interface DiscountCodeInputProps {
  onApply: (code: string) => void;
  placeholder?: string;
  buttonText?: string;
  activeCodes?: (string | ActiveCode)[];
  showActiveCodes?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
  error?: string | null;
  onClearError?: () => void;
}

const DiscountCodeInput: React.FC<DiscountCodeInputProps> = ({
  onApply,
  placeholder = '',
  buttonText = 'Apply',
  activeCodes = [],
  showActiveCodes = true,
  isLoading = false,
  disabled = false,
  value: externalValue,
  onChange: externalOnChange,
  className = '',
  inputClassName = '',
  buttonClassName = '',
  error = null,
  onClearError = undefined,
}) => {
  const [internalValue, setInternalValue] = useState('');

  const isControlled = externalValue !== undefined;
  const inputValue = isControlled ? externalValue || '' : internalValue;
  const setInputValue = isControlled && externalOnChange ? externalOnChange : setInternalValue;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (error && onClearError) {
      onClearError();
    }
  };

  const handleApply = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || disabled || isLoading) return;

    onApply(trimmedValue);

    if (!isControlled) {
      setInternalValue('');
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (error && onClearError) {
          onClearError();
        }
      }
    };

    document.addEventListener(CLICK_OUTSIDE_EVENT, handleClickOutside);
    return () => {
      document.removeEventListener(CLICK_OUTSIDE_EVENT, handleClickOutside);
    };
  }, [error, onClearError]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`w-full ${className}`}
    >
      {(!showActiveCodes || activeCodes.length === 0) && (
        <div className="flex gap-2 w-full">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            className={`
            flex-1 
            min-w-0
            text-sm 
            focus:outline-none 
            disabled:bg-gray-100 
            disabled:cursor-not-allowed
            ${inputClassName}
          `}
            style={{
              display: 'flex',
              height: '36px',
              padding: '8px 12px',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'stretch',
              borderRadius: 'var(--System-Radius__inputs, 6px)',
              border: error ? '1px solid #DB0000' : '1px solid var(--Neutral_grey-500, #667085)',
              background: 'var(--Core-Colours-Backgrounds-Neutral_grey-BgNeutral_grey_white, #FFF)'
            }}
            aria-invalid={!!error}
            aria-label="Discount code input"
          />
          <button
            onClick={handleApply}
            disabled={!inputValue.trim() || disabled || isLoading}
            style={{
              display: 'flex',
              height: '36px',
              padding: 'var(--Spacing-8, 8px) var(--Spacing-16, 16px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--Spacing-6, 6px)',
              borderRadius: 'var(--System-Radius__button, 6px)',
              background: 'var(--Buttons-Buttons-Default-Backgrounds-BGButtonDefault_default, #1A6FE5)',
              color: 'var(--Buttons-Buttons-Default-Other-TextButtonFilled, #FFF)',
              fontFamily: 'var(--1_Typography-Additional-Button-Sm-Typeface, "Noto Sans")',
              fontSize: 'var(--1_Typography-Additional-Button-Sm-Font_size, 12px)',
              fontStyle: 'normal',
              fontWeight: 'var(--1_Typography-Additional-Button-Sm-Font_weight, 700)',
              lineHeight: 'var(--1_Typography-Additional-Button-Sm-Line_height, 18px)',
              border: 'none',
              cursor: (!inputValue.trim() || disabled || isLoading) ? 'not-allowed' : 'pointer'
            }}
            className={buttonClassName}
            aria-label="Apply discount code"
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin h-4 w-4 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Applying...
              </span>
            ) : (
              buttonText
            )}
          </button>
        </div>
      )}

      {error && (
        <div
          className="mt-[6px]"
          style={{
            color: 'var(--Core-Colours-Text-Error-TextError_base, #DB0000)',
            fontFamily: 'var(--1_Typography-Body-Xs-Xs_Regular-Typeface, "Noto Sans")',
            fontSize: 'var(--1_Typography-Body-Xs-Xs_Regular-Font_size, 12px)',
            fontStyle: 'normal',
            fontWeight: 'var(--1_Typography-Body-Xs-Xs_Regular-Font_weight, 400)',
            lineHeight: 'var(--1_Typography-Body-Xs-Xs_Regular-Line_height, 18px)'
          }}
        >
          {error}
        </div>
      )}

      {showActiveCodes && activeCodes.length > 0 && (
        <div className="mt-3 space-y-2">
          {activeCodes.map((codeInfo, index) => (
            <DiscountApplied
              key={index}
              code={typeof codeInfo === 'string' ? codeInfo : codeInfo.code}
              discount={typeof codeInfo === 'object' && codeInfo.discount ? codeInfo.discount : '-'}
              onRemove={typeof codeInfo === 'object' ? codeInfo.onRemove : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DiscountCodeInput;
